/* eslint-disable lingui/no-unlocalized-strings -- Approved First Round plan copy (PLAN.md §3). */
import type {
  FirstRoundMe,
  FirstRoundPlanCard,
  FirstRoundPlanKey,
} from "@/client/services/pricing/first-round";

/*
 * What the First Round plan cards say and do. The numbers come from the database (admin edits
 * show everywhere); the wording is the approved copy in first-round-pricing/PLAN.md §3.
 */

const count = (n: number | null, one: string, many: string, unlimited: string) =>
  n === null ? unlimited : `${n} ${n === 1 ? one : many}`;

const accessLine = (days: number | null) =>
  days ? `${days} days of access, starting with your first practice interview` : null;

export const planLines = (card: FirstRoundPlanCard): string[] => {
  const { resumes, practiceInterviews, demoTries } = card.limits;
  const resumeCount = count(resumes, "resume", "resumes", "Unlimited resumes");
  const resumesUpTo = resumes === null ? resumeCount : `Up to ${resumeCount}`;
  const interviews = count(
    practiceInterviews,
    "AI-coached practice interview",
    "AI-coached practice interviews",
    "Unlimited AI-coached practice interviews",
  );

  switch (card.key) {
    case "free": {
      return [
        `${resumesUpTo}: create, edit and download`,
        "Tailor your resume to each job",
        demoTries === 0
          ? null
          : `2-minute practice interview demo, ${
              demoTries === null
                ? "any number of tries"
                : `up to ${count(demoTries, "try", "tries", "")}`
            }`,
      ].filter((line): line is string => Boolean(line));
    }
    case "starter": {
      return [
        accessLine(card.accessDays),
        `${resumesUpTo}: create, edit, download and tailor to jobs`,
        interviews,
        "Record and watch your video",
        "Curated feedback on every answer",
        "Practise for your own jobs",
      ].filter((line): line is string => Boolean(line));
    }
    case "active": {
      return [accessLine(card.accessDays), resumesUpTo, interviews, "Everything in Starter"].filter(
        (line): line is string => Boolean(line),
      );
    }
  }
};

export const PLAN_FOOTNOTES: Record<FirstRoundPlanKey, string> = {
  free: "No credit card required",
  starter: "Best for one upcoming interview",
  active: "For when you have multiple roles",
};

export const ENTERPRISE_CARD = {
  name: "Enterprise",
  price: "Contact",
  description: "For universities, colleges and employment programs.",
  lines: [
    "Practice access for a whole cohort",
    "Usage reporting from our team",
    "Set up and supported by our team",
  ],
  cta: "Contact sales",
  footnote: "Book your free demo",
};

export type CardAction =
  | { kind: "current"; label: string }
  | { kind: "included"; label: string }
  | { kind: "free"; label: string }
  | { kind: "buy"; label: string; planId: string }
  | { kind: "upgrade"; label: string; planId: string; amount: number };

/** The button on a card for this user: their plan, an upgrade, a purchase, or nothing to do. */
export const cardAction = (
  card: FirstRoundPlanCard,
  me: FirstRoundMe | null | undefined,
  formatPrice: (amount: number, currency: string) => string,
): CardAction => {
  const pass = me?.pass ?? null;
  if (pass?.planKey === card.key) return { kind: "current", label: "Your plan" };

  if (card.key === "free") {
    if (pass) return { kind: "included", label: "Included in your pass" };
    if (me?.planChosen) return { kind: "current", label: "Your plan" };
    return { kind: "free", label: "Continue with Free" };
  }

  if (card.key === "starter" && pass?.planKey === "active") {
    return { kind: "included", label: "Included in Active" };
  }

  if (card.key === "active" && pass?.planKey === "starter") {
    if (me?.upgrade && me.upgrade.toPlanId === card.id) {
      return {
        kind: "upgrade",
        label: `Upgrade for ${formatPrice(me.upgrade.amount, me.upgrade.currency)}`,
        planId: card.id,
        amount: me.upgrade.amount,
      };
    }
    return { kind: "included", label: "Not available on your pass" };
  }

  return { kind: "buy", label: `Choose ${card.name}`, planId: card.id };
};
